<script setup lang="ts">
import { computed, provide } from 'vue'
import type { BookPageConfig } from '../configs/types'
import BizPopupBookTOCFlipnovel from '@/components/BizPopupBookTOC.flipnovel.vue'
import { theme as brandTheme } from '@/config/brands'
import { formatCompactNumber } from '@/utils'
import { t } from '@/i18n/utils'

interface Props {
  book: GetContentGetcontentdetailResponse['data']
  config: BookPageConfig
  coverUrl?: string
  introduce?: string
  paragraphs: string[]
  popupTOC: boolean
  bannerShow: boolean
  isInShelf?: boolean
  shelfLoading?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'toggle-shelf'): void
}>()

const tagList = computed(() => (props.book.tagList || []).filter((t): t is { id: string; name: string } => !!t.name))

provide('theme', { background: brandTheme.bgGlobal || '#121214' })

// 创建可写的 computed 用于 v-model 双向绑定
const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})

const coverMaskBackground = computed(() => {
  const bg = brandTheme.bgGlobal || '#14181F'
  const maskColor = brandTheme.components?.bookPage?.coverMaskColor || '18,18,20'
  return `linear-gradient(180deg, rgba(${maskColor},0.18) 0%, rgba(${maskColor},0.42) 28%, rgba(${maskColor},0.76) 58%, rgba(${maskColor},0.94) 82%, ${bg} 100%)`
})

const ctaBackground = computed(() => brandTheme.primaryGradient || 'linear-gradient(90deg, #FF8A4C 0%, #FF5A36 100%)')
const introMoreBgColor = computed(() => brandTheme.bgGlobal || '#14181F')
const cardBgColor = computed(() => brandTheme.bgCard || '#1D232D')
const coverShadow = computed(() =>
  brandTheme.components?.bookPage?.coverShadow || '0 8px 32px rgba(152, 117, 251, 0.35), 0 2px 8px rgba(0, 0, 0, 0.4)')
const tocBorderColor = computed(() =>
  brandTheme.components?.bookPage?.tocBorderColor || 'rgba(255, 255, 255, 0.1)')
</script>

<template>
  <div class="rr-book-page bg-n-bg-global text-n-text-1">
    <!-- ==================== 封面区域 ==================== -->
    <div
      class="rr-book-hero relative overflow-hidden pt-64px pb-24px"
      :class="{ 'mt-60px': bannerShow }"
    >
      <!-- 背景层：书封高斯模糊 -->
      <div class="rr-book-hero-bg" :style="`background-image: url(${coverUrl})`" />
      <!-- 模糊效果 -->
      <div class="rr-book-hero-blur" />
      <!-- 深色渐变遮罩：从上到下 -->
      <div class="rr-book-hero-mask" :style="{ background: coverMaskBackground }" />

      <!-- 封面内容 -->
      <div class="rr-book-hero-content px-16px text-center">
        <!-- 书封 -->
        <img
          class="w-140px h-186px mx-auto rounded-8px object-cover"
          :style="{ boxShadow: coverShadow, position: 'relative' }"
          :src="coverUrl"
          alt="Book Cover"
        >
        <!-- 书名 -->
        <h1 class="mt-20px text-22px font-bold text-n-text-1 lh-30px">
          {{ book.contentName }}
        </h1>
        <!-- 作者 -->
        <p class="mt-8px text-14px text-n-text-2">
          {{ book.authorInfo?.author }}
        </p>

        <!-- 书籍信息栏（放在封面区域内，避免外部负 margin 导致的层叠问题） -->
        <div class="mt-16px text-center text-13px text-n-text-2">
          <i-icon-park-outline-list-view class="w-4 h-4 text-n-text-2 inline-block align-middle" />
          <span class="align-middle ml-6px">{{ book.lastChapter?.sequenceNo || 0 }} {{ config.texts.chapters }}</span>
          <span class="inline-block w-1px h-12px bg-n-border-primary mx-12px align-middle" />
          <i-icon-park-outline-time class="w-4 h-4 text-n-text-2 inline-block align-middle" />
          <span class="align-middle ml-6px">{{ book.finishStatus ? config.texts.completed : config.texts.ongoing }}</span>
          <template v-if="book.viewUsers">
            <span class="inline-block w-1px h-12px bg-n-border-primary mx-12px align-middle" />
            <i-assets-icon-view-count class="w-18px h-14px text-n-text-2 inline-block align-middle" />
            <span class="align-middle ml-6px">{{ formatCompactNumber(book.viewUsers) }}</span>
          </template>
        </div>
      </div>
    </div>

    <!-- ==================== 简介卡片 ==================== -->
    <div class="rr-module-card mt-16px mx-16px px-16px pt-16px pb-20px">
      <!-- 标签 -->
      <div v-if="tagList.length" class="flex items-center flex-nowrap overflow-x-auto gap-8px text-13px mb-12px rr-hide-scrollbar">
        <div
          v-for="tag in tagList"
          :key="tag.id"
          class="flex-shrink-0 whitespace-nowrap px-10px py-4px rounded-full cursor-pointer bg-n-primary/15 text-n-primary h-28px flex items-center"
          @click="emit('goto-tags', tag.id || '')"
        >
          {{ tag.name }}
        </div>
      </div>

      <!-- 简介标题 -->
      <h2 class="text-16px font-bold text-n-text-1 mb-10px">
        {{ config.texts.bookIntroduce }}
      </h2>

      <!-- 简介文本（可展开） -->
      <BasicExpandableText
        :text="book.introduce"
        :line-clamp="6"
        text-class="text-14px lh-24px text-n-text-2"
        more-class="text-n-primary cursor-pointer text-13px font-medium"
        :more-bg-color="cardBgColor"
      />
    </div>

    <!-- ==================== 章节信息 ==================== -->
    <div class="rr-module-card mt-12px mx-16px px-16px py-14px">
      <div
        class="h-24px flex items-center justify-between cursor-pointer"
        @click="emit('open-toc')"
      >
        <span class="text-15px font-bold text-n-text-1">{{ config.texts.catalogue }}</span>
        <div class="flex items-center gap-4px text-13px text-n-text-2">
          <span class="line-clamp-1 max-w-180px">{{ book.lastChapter?.chapterName }}</span>
          <i-icon-park-outline-right class="w-4 h-4" />
        </div>
      </div>
    </div>

    <!-- ==================== 章节预览 ==================== -->
    <div class="rr-module-card mt-12px mx-16px mb-92px px-16px pt-14px pb-24px">
      <!-- 引导语 -->
      <div v-if="introduce" class="mb-16px text-14px lh-24px text-n-text-2">
        <p v-html="introduce" />
      </div>

      <!-- 章节标题 -->
      <h3 class="text-16px font-bold text-n-text-1 mb-12px">
        {{ book.firstChapter?.chapterName }}
      </h3>

      <!-- 章节内容 -->
      <div class="lh-28px text-15px text-n-text-2">
        <p
          v-for="(item, index) in paragraphs.slice(0, 16)"
          :key="index"
          class="mt-12px"
        >
          {{ item }}
        </p>
      </div>

      <!-- 结束分割线 -->
      <div class="mt-24px flex items-center justify-center gap-12px">
        <div class="h-1px w-40px bg-n-border-primary" />
        <span class="text-12px text-n-text-3">{{ config.texts.readFirstChapter }}</span>
        <div class="h-1px w-40px bg-n-border-primary" />
      </div>
    </div>

    <!-- ==================== TOC 弹窗 ==================== -->
    <BizPopupBookTOCFlipnovel
      v-if="book && book.firstChapterContentUrl"
      v-model="popupTOCModel"
      :book="book"
      :content-id="book.id"
      :chapter-id="book.firstChapter?.id"
      :style="{ '--toc-border-color': tocBorderColor }"
    />

    <!-- ==================== 底部固定按钮 ==================== -->
    <div class="rr-book-cta-wrap">
      <div class="w-full flex items-center gap-12px px-16px py-10px" :style="{ background: introMoreBgColor }">
        <div
          class="flex flex-col items-center justify-center min-w-50px py-2px cursor-pointer flex-shrink-0"
          :class="{ 'opacity-50': shelfLoading }"
          :style="{ color: isInShelf ? brandTheme.primaryColor : '#A5A5A5' }"
          @click="emit('toggle-shelf')"
        >
          <i-assets-book-added-shelf v-if="isInShelf" class="w-22px h-22px pointer-events-none" />
          <i-assets-book-add-shelf v-else class="w-22px h-22px pointer-events-none" />
          <p class="mt-2px text-10px lh-11px">
            {{ isInShelf ? t('BookshelfAdded') : t('Bookshelf') }}
          </p>
        </div>
        <div
          class="flex-1 h-48px lh-48px text-center text-16px font-bold rounded-full cursor-pointer"
          :style="{ background: ctaBackground, color: '#FFFFFF !important' }"
          @click="emit('read')"
        >
          {{ !book.readFlag ? config.texts.startReading : config.texts.continueReading }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.rr-book-hero {
  position: relative;
  isolation: isolate;
  background: v-bind(introMoreBgColor);
}

.rr-book-hero::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 44px;
  background: linear-gradient(180deg, transparent 0%, v-bind(introMoreBgColor) 100%);
  pointer-events: none;
  z-index: 3;
}

.rr-book-hero-bg,
.rr-book-hero-blur,
.rr-book-hero-mask {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.rr-book-hero-bg {
  z-index: 0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  transform: scale(1.04);
}

.rr-book-hero-blur {
  z-index: 1;
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
}

.rr-book-hero-mask {
  z-index: 2;
}

.rr-book-hero-content {
  position: relative;
  z-index: 4;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.rr-module-card {
  border-radius: 16px;
  background: v-bind(cardBgColor);
  border: 1px solid var(--rr-stroke);
}

.rr-hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.rr-hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.rr-book-cta-wrap {
  position: fixed;
  z-index: 30;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 750px;
  margin: 0 auto;
  box-sizing: border-box;
}

.rr-book-page {
  overflow-x: hidden;
  background: v-bind(introMoreBgColor) !important;
}

@media (min-width: 768px) {
  .rr-book-cta-wrap {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
  }
}
</style>
